import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateApiKey } from '@/lib/api/apiKeys';
import { Copy, Check } from 'lucide-react';

interface TokenFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
}

export function TokenFormDialog({ open, onOpenChange, mode }: TokenFormDialogProps) {
    const { t } = useTranslation('settings');
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [orgRole, setOrgRole] = React.useState('org:support');
    const [createdToken, setCreatedToken] = React.useState<string | null>(null);
    const [copied, setCopied] = React.useState(false);

    const createMutation = useCreateApiKey();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const result = await createMutation.mutateAsync({
                name,
                description: description || null,
                expiresAt: null,
                permissions: [],
                orgRole,
            });

            // Store the created token (UUID) to show to user
            setCreatedToken(result.token);
        } catch (error) {
            console.error('Error creating token:', error);
        }
    };

    const handleCopy = async () => {
        if (createdToken) {
            await navigator.clipboard.writeText(createdToken);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleClose = () => {
        setName('');
        setDescription('');
        setOrgRole('org:support');
        setCreatedToken(null);
        setCopied(false);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {createdToken ? t('accessTokens.form.titleCreated') : t('accessTokens.form.title')}
                    </DialogTitle>
                </DialogHeader>

                {createdToken ? (
                    <div className="space-y-4">
                        <div className="rounded-lg bg-muted p-4">
                            <p className="text-sm text-muted-foreground mb-2">
                                {t('accessTokens.form.copyWarning')}
                            </p>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 rounded bg-background px-2 py-1 text-sm break-all">
                                    {createdToken}
                                </code>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                >
                                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <Button onClick={handleClose} className="w-full">
                            {t('accessTokens.form.done')}
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t('accessTokens.form.nameLabel')}</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder={t('accessTokens.form.namePlaceholder')}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">{t('accessTokens.form.descriptionLabel')}</Label>
                            <Textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('accessTokens.form.descriptionPlaceholder')}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="role">{t('accessTokens.form.roleLabel')}</Label>
                            <Select value={orgRole} onValueChange={setOrgRole}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="org:admin">
                                        {t('accessTokens.form.roleAdmin')}
                                    </SelectItem>
                                    <SelectItem value="org:support">
                                        {t('accessTokens.form.roleSupport')}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                                {t('accessTokens.form.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={createMutation.isPending}
                                className="flex-1"
                            >
                                {createMutation.isPending ? t('accessTokens.form.creating') : t('accessTokens.form.createToken')}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
